<script lang="ts" setup>import { toRef as _toRef, computed as _computed } from 'vue';

import type { OrderGift } from '@consumer/types/serializers'

import {
  faCalendar, faEnvelope,
  faGift,
  faMailboxFlagUp,
  faMessageLines,
  faPrint,
} from '@fortawesome/pro-light-svg-icons'

import { formatCurrency } from '@consumer/utils/currency'
import { formatDate, formatDeliveryDate, isToday } from '@consumer/utils/date'
import { useElementSize } from '@vueuse/core'
import { formatPhoneNumber } from '@consumer/utils/phoneNumber'
import { isContact } from '@consumer/utils/types'
import { formatMailingAddress } from '@consumer/utils/address'
import { isVerifiedRecipientAddress } from '@consumer/utils/verified'

withDefaults(defineProps<{
  compact?: boolean
  orderGift: OrderGift
}>(), {  })

const containerEl = useTemplateRef<HTMLElement>('container')

const __$temp_1 = (useElementSize(containerEl)),
  containerWidth = _toRef(__$temp_1, 'width');

const expanded = _computed(() => containerWidth.value > 400)
const lineGap = _computed(() => __props.compact ? 'gap-1' : 'gap-2')

const delivery = _computed(() => __props.orderGift.delivery)
const greeting = _computed(() => __props.orderGift.greeting)
const mailingAddress = _computed(() => delivery.value.recipientMailingAddress)

const iconsByDeliveryMethod = {
  email: faEnvelope,
  text: faMessageLines,
  mail: faMailboxFlagUp,
  print: faPrint,
}
</script>

<template>
  <div
    ref="container"
    class="order-gift-content text-sm flex flex-col flex-wrap text-v2-secondary font-normal"
    :class="lineGap"
    style="min-width: 0;"
  >
    <div class="text-lg font-semibold text-navy flex items-center">
      <div class="flex items-center gap-2">
        <slot name="title">{{ greeting?.to ?? 'Gift Recipient' }}</slot>

        <GBadge
          v-if="delivery.recipient?.usernameWithAt"
          class="text-xs !bg-white
            !border-v2-secondary !border-opacity-50 border-1 !text-v2-secondary font-normal
            !pl-1
          "
          size="sm"
          type="chip"
          :label="delivery.recipient.usernameWithAt"
        >
          <template #pre>
            <ProfilePicture :user="delivery.recipient as any" :size="16" newLayout class="!rounded-1"/>
          </template>
        </GBadge>
        <GBadge
          v-if="isContact(delivery.recipient)"
          class="text-xs !bg-white
            !border-v2-secondary !border-opacity-50 border-1 !text-v2-secondary font-normal
          "
          size="sm"
          type="chip"
          label="Contact"
        />

      </div>
      <div v-if="$slots.actions" class="ml-auto">
        <slot name="actions"/>
      </div>
    </div>
    <div class="flex gap-2 items-center">
      <GIcon :icon="faGift" title="Gift"/>
      <div>
        {{ formatCurrency(orderGift.amount) }}
      </div>
      <!-- <div v-if="compact && !greeting?.to" class="ml-auto">
        <slot name="actions"/>
      </div> -->
    </div>
    <div class="flex w-full" :class="expanded ? 'gap-2 items-center' : `${lineGap} flex-col`">
      <div class="flex items-center gap-2">
        <GIcon
          v-if="delivery.deliveryMethod"
          :icon="iconsByDeliveryMethod[delivery.deliveryMethod]"
          :title="delivery.deliveryMethod"
        />
        <div class="truncate" style="max-width: 30ch;">
          <template v-if="delivery.deliveryMethod === 'email'">
            <div v-if="isVerifiedRecipientAddress(delivery)">Verified email address</div>
            <div v-else class="truncate">{{ delivery.recipientEmail }}</div>
          </template>
          <template v-else-if="delivery.deliveryMethod === 'text'">
            <div v-if="isVerifiedRecipientAddress(delivery)">Verified phone number</div>
            <div v-else>{{ formatPhoneNumber(delivery.recipientPhoneNumber) }}</div>
          </template>
          <template v-else-if="delivery.deliveryMethod === 'mail'">
            <div v-if="isVerifiedRecipientAddress(delivery)">Verified mailing address</div>
            <div v-else>
              {{ formatMailingAddress(mailingAddress) }}
            </div>
          </template>
          <template v-else>
            Printable PDF
          </template>
        </div>
      </div>

      <div v-if="expanded">&bull;</div>

      <div class="flex gap-2 items-center">
        <GIcon v-if="!expanded" :icon="faCalendar" title="Calendar"/>
        <div class="truncate">
          <template v-if="delivery.deliveryMethod === 'mail'">
            Estimated delivery by {{ formatDate(delivery.estimatedDeliveryDate) }}
          </template>
          <template
            v-else-if="delivery.deliveryMethod === 'print' || !delivery.deliverAt || isToday(delivery.deliverAt)"
          >
            Deliver after checkout
          </template>
          <template v-else>
            Deliver on {{ formatDeliveryDate(delivery) }}
          </template>
        </div>
      </div>

      <slot/>
    </div>
  </div>
</template>
